import { initializeApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
  getToken,
} from 'firebase/app-check';
import { getFirestore } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getAuth } from 'firebase/auth';
import { connectAuthEmulator } from 'firebase/auth';
import { connectFirestoreEmulator } from 'firebase/firestore';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { connectStorageEmulator } from 'firebase/storage';
import {
  assertLocalFirebaseEnvironment,
  LOCAL_EMULATOR_HOST,
  LOCAL_EMULATOR_PORTS,
} from './firebaseEmulators';

const requireFirebaseConfig = (
  name: string,
  value: string | undefined,
): string => {
  if (!value || value.startsWith('REPLACE_WITH_')) {
    throw new Error(`Missing Firebase configuration: ${name}`);
  }

  return value;
};

const firebaseConfig = {
  apiKey: requireFirebaseConfig(
    'REACT_APP_FIREBASE_API_KEY',
    process.env.REACT_APP_FIREBASE_API_KEY,
  ),
  authDomain: requireFirebaseConfig(
    'REACT_APP_FIREBASE_AUTH_DOMAIN',
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  ),
  projectId: requireFirebaseConfig(
    'REACT_APP_FIREBASE_PROJECT_ID',
    process.env.REACT_APP_FIREBASE_PROJECT_ID,
  ),
  storageBucket: requireFirebaseConfig(
    'REACT_APP_FIREBASE_STORAGE_BUCKET',
    process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  ),
  messagingSenderId: requireFirebaseConfig(
    'REACT_APP_FIREBASE_MESSAGING_SENDER_ID',
    process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  ),
  appId: requireFirebaseConfig(
    'REACT_APP_FIREBASE_APP_ID',
    process.env.REACT_APP_FIREBASE_APP_ID,
  ),
};

const functionsRegion = requireFirebaseConfig(
  'REACT_APP_FIREBASE_FUNCTIONS_REGION',
  process.env.REACT_APP_FIREBASE_FUNCTIONS_REGION,
);

const app = initializeApp(firebaseConfig);
const localEmulators = process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true';
if (localEmulators)
  assertLocalFirebaseEnvironment({
    projectId: firebaseConfig.projectId,
    useEmulators: process.env.REACT_APP_USE_FIREBASE_EMULATORS,
    host: process.env.REACT_APP_FIREBASE_EMULATOR_HOST,
    authPort: process.env.REACT_APP_FIREBASE_AUTH_EMULATOR_PORT,
    firestorePort: process.env.REACT_APP_FIREBASE_FIRESTORE_EMULATOR_PORT,
    functionsPort: process.env.REACT_APP_FIREBASE_FUNCTIONS_EMULATOR_PORT,
    storagePort: process.env.REACT_APP_FIREBASE_STORAGE_EMULATOR_PORT,
  });
const appCheck = localEmulators
  ? undefined
  : initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(
        requireFirebaseConfig(
          'REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY',
          process.env.REACT_APP_RECAPTCHA_ENTERPRISE_SITE_KEY,
        ),
      ),
      isTokenAutoRefreshEnabled: true,
    });

export const appCheckHeaders = async (): Promise<Record<string, string>> =>
  appCheck ? { 'X-Firebase-AppCheck': (await getToken(appCheck)).token } : {};

const db = getFirestore(app);
const storage = getStorage(app);
const auth = getAuth(app);
const functions = getFunctions(app, functionsRegion);

if (process.env.REACT_APP_USE_FIREBASE_EMULATORS === 'true') {
  connectAuthEmulator(
    auth,
    `http://${LOCAL_EMULATOR_HOST}:${LOCAL_EMULATOR_PORTS.auth}`,
    {
      disableWarnings: true,
    },
  );
  connectFirestoreEmulator(
    db,
    LOCAL_EMULATOR_HOST,
    LOCAL_EMULATOR_PORTS.firestore,
  );
  connectStorageEmulator(
    storage,
    LOCAL_EMULATOR_HOST,
    LOCAL_EMULATOR_PORTS.storage,
  );
  connectFunctionsEmulator(
    functions,
    LOCAL_EMULATOR_HOST,
    LOCAL_EMULATOR_PORTS.functions,
  );
} else if (process.env.REACT_APP_USE_FUNCTIONS_EMULATOR === 'true') {
  connectFunctionsEmulator(functions, window.location.hostname, 5001);
}

export { db, storage, auth, functions };
